"use client";

import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  ReactNode,
} from "react";
import { useAuth } from "./auth-provider";
import { messagingService } from "@/lib/domain/social/services/messaging.service";
import {
  Message,
  Conversation,
  Contact,
  MessageAttachment,
} from "@/types/messaging";

// State interface
interface MessagingState {
  conversations: Conversation[];
  activeConversation: Conversation | null;
  messages: Record<string, Message[]>; // conversationId -> messages[]
  contacts: Contact[];
  isLoading: boolean;
  error: string | null;
  typingUsers: Record<string, string[]>; // conversationId -> userNames[]
}

// Action types
type MessagingAction =
  | { type: "SET_LOADING"; payload: boolean }
  | { type: "SET_ERROR"; payload: string | null }
  | { type: "SET_CONVERSATIONS"; payload: Conversation[] }
  | { type: "SET_ACTIVE_CONVERSATION"; payload: Conversation | null }
  | {
      type: "SET_MESSAGES";
      payload: { conversationId: string; messages: Message[] };
    }
  | {
      type: "ADD_MESSAGE";
      payload: { conversationId: string; message: Message };
    }
  | { type: "SET_CONTACTS"; payload: Contact[] }
  | {
      type: "SET_TYPING_USERS";
      payload: { conversationId: string; users: string[] };
    }
  | { type: "UPDATE_CONVERSATION"; payload: Conversation }
  | { type: "RESET" };

// Initial state
const initialState: MessagingState = {
  conversations: [],
  activeConversation: null,
  messages: {},
  contacts: [],
  isLoading: false,
  error: null,
  typingUsers: {},
};

// Reducer
function messagingReducer(
  state: MessagingState,
  action: MessagingAction
): MessagingState {
  switch (action.type) {
    case "SET_LOADING":
      return { ...state, isLoading: action.payload };

    case "SET_ERROR":
      return { ...state, error: action.payload, isLoading: false };

    case "SET_CONVERSATIONS":
      return { ...state, conversations: action.payload };

    case "SET_ACTIVE_CONVERSATION":
      return { ...state, activeConversation: action.payload };

    case "SET_MESSAGES":
      return {
        ...state,
        messages: {
          ...state.messages,
          [action.payload.conversationId]: action.payload.messages,
        },
      };

    case "ADD_MESSAGE":
      const { conversationId, message } = action.payload;
      const existingMessages = state.messages[conversationId] || [];

      // Check if message already exists
      const messageExists = existingMessages.some((m) => m.id === message.id);
      if (messageExists) return state;

      const updatedMessages = [...existingMessages, message].sort(
        (a, b) => a.timestamp - b.timestamp
      );

      return {
        ...state,
        messages: {
          ...state.messages,
          [conversationId]: updatedMessages,
        },
      };

    case "SET_CONTACTS":
      return { ...state, contacts: action.payload };

    case "SET_TYPING_USERS":
      return {
        ...state,
        typingUsers: {
          ...state.typingUsers,
          [action.payload.conversationId]: action.payload.users,
        },
      };

    case "UPDATE_CONVERSATION":
      const updatedConversations = state.conversations.map((conv) =>
        conv.id === action.payload.id ? action.payload : conv
      );

      // If conversation doesn't exist, add it
      if (!state.conversations.find((conv) => conv.id === action.payload.id)) {
        updatedConversations.push(action.payload);
      }

      // Sort by last message timestamp
      updatedConversations.sort((a, b) => {
        const aTime = a.lastMessage?.timestamp || 0;
        const bTime = b.lastMessage?.timestamp || 0;
        return bTime - aTime;
      });

      return {
        ...state,
        conversations: updatedConversations,
        activeConversation:
          state.activeConversation?.id === action.payload.id
            ? action.payload
            : state.activeConversation,
      };

    case "RESET":
      return initialState;

    default:
      return state;
  }
}

// Context
interface MessagingContextType {
  state: MessagingState;
  actions: {
    loadConversations: () => Promise<void>;
    loadContacts: () => Promise<void>;
    selectConversation: (conversation: Conversation) => void;
    sendDirectMessage: (
      recipientId: string,
      recipientName: string,
      content: string,
      attachments?: MessageAttachment[]
    ) => Promise<void>;
    sendLeagueMessage: (
      leagueId: string,
      content: string,
      attachments?: MessageAttachment[]
    ) => Promise<void>;
    sendLeagueAnnouncement: (
      leagueId: string,
      announcement: {
        title: string;
        content: string;
        type: "general" | "event" | "achievement" | "urgent";
        priority: "low" | "normal" | "high";
      }
    ) => Promise<void>;
    setTyping: (conversationId: string, isTyping: boolean) => void;
    moderateMember: (
      leagueId: string,
      memberId: string,
      action: "mute" | "unmute" | "ban" | "unban" | "promote" | "demote"
    ) => Promise<void>;
    deleteMessage: (messageId: string) => Promise<void>;
    pinMessage: (messageId: string) => Promise<void>;
    markAsRead: (conversationId: string) => Promise<void>;
    getLeagueMembers: (leagueId: string) => Promise<any[]>;
    clearError: () => void;
  };
}

const MessagingContext = createContext<MessagingContextType | undefined>(
  undefined
);

// Provider component
interface MessagingProviderProps {
  children: ReactNode;
}

export function MessagingProvider({ children }: MessagingProviderProps) {
  const [state, dispatch] = useReducer(messagingReducer, initialState);
  const { user } = useAuth();
  const messageUnsubscribers = React.useRef<Map<string, () => void>>(new Map());
  const conversationUnsubscriber = React.useRef<(() => void) | null>(null);

  // Load conversations when user changes
  useEffect(() => {
    if (user?.id) {
      // Subscribe to real-time conversation updates
      conversationUnsubscriber.current =
        messagingService.subscribeToConversations(user.id, (conversations) => {
          dispatch({ type: "SET_CONVERSATIONS", payload: conversations });
        });

      loadContacts();
    } else {
      dispatch({ type: "RESET" });
    }

    // Cleanup on unmount
    return () => {
      messageUnsubscribers.current.forEach((unsubscribe) => unsubscribe());
      messageUnsubscribers.current.clear();

      if (conversationUnsubscriber.current) {
        conversationUnsubscriber.current();
        conversationUnsubscriber.current = null;
      }

      messagingService.cleanup();
    };
  }, [user?.id]);

  // Actions
  const loadConversations = async () => {
    if (!user?.id) return;

    try {
      dispatch({ type: "SET_LOADING", payload: true });
      const conversations = await messagingService.getConversations(user.id);
      dispatch({ type: "SET_CONVERSATIONS", payload: conversations });
    } catch (error) {
      console.error("Failed to load conversations:", error);
      dispatch({ type: "SET_ERROR", payload: "Failed to load conversations" });
    } finally {
      dispatch({ type: "SET_LOADING", payload: false });
    }
  };

  const loadContacts = async () => {
    if (!user?.id) return;

    try {
      const contacts = await messagingService.getContacts(user.id);
      dispatch({ type: "SET_CONTACTS", payload: contacts });
    } catch (error) {
      console.error("Failed to load contacts:", error);
      dispatch({ type: "SET_ERROR", payload: "Failed to load contacts" });
    }
  };

  const selectConversation = (conversation: Conversation) => {
    dispatch({ type: "SET_ACTIVE_CONVERSATION", payload: conversation });

    // Mark conversation as read
    if (user?.id) {
      messagingService
        .markAsRead(user.id, conversation.id)
        .catch((err) =>
          console.error("Failed to mark conversation as read:", err)
        );
    }

    // Subscribe to messages for this conversation if not already subscribed
    const conversationId = getConversationId(conversation);
    if (!messageUnsubscribers.current.has(conversationId)) {
      const unsubscribe = messagingService.subscribeToMessages(
        user?.id || "",
        getConversationTargetId(conversation),
        conversation.type,
        (messages) => {
          dispatch({
            type: "SET_MESSAGES",
            payload: { conversationId, messages },
          });
        }
      );

      messageUnsubscribers.current.set(conversationId, unsubscribe);
    }
  };

  const sendDirectMessage = async (
    recipientId: string,
    recipientName: string,
    content: string,
    attachments?: MessageAttachment[]
  ) => {
    if (!user?.id || !user?.name) throw new Error("User not authenticated");

    await messagingService.sendDirectMessage(
      user.id,
      user.name,
      recipientId,
      recipientName,
      content,
      attachments
    );

    // Reload conversations to update last message
    loadConversations();
  };

  const sendLeagueMessage = async (
    leagueId: string,
    content: string,
    attachments?: MessageAttachment[]
  ) => {
    if (!user?.id || !user?.name) throw new Error("User not authenticated");

    await messagingService.sendLeagueMessage(
      user.id,
      user.name,
      leagueId,
      content,
      attachments
    );

    // Reload conversations to update last message
    loadConversations();
  };

  const sendLeagueAnnouncement = async (
    leagueId: string,
    announcement: {
      title: string;
      content: string;
      type: "general" | "event" | "achievement" | "urgent";
      priority: "low" | "normal" | "high";
    }
  ) => {
    if (!user?.id || !user?.name) throw new Error("User not authenticated");

    await messagingService.sendLeagueAnnouncement(
      user.id,
      user.name,
      leagueId,
      announcement
    );

    // Reload conversations to update last message
    loadConversations();
  };

  const setTyping = (conversationId: string, isTyping: boolean) => {
    if (!user?.id) return;
    messagingService.setTyping(user.id, conversationId, isTyping);
  };

  const moderateMember = async (
    leagueId: string,
    memberId: string,
    action: "mute" | "unmute" | "ban" | "unban" | "promote" | "demote"
  ) => {
    if (!user?.id) throw new Error("User not authenticated");

    await messagingService.moderateMember(leagueId, user.id, memberId, action);
  };

  const deleteMessage = async (messageId: string) => {
    if (!user?.id) throw new Error("User not authenticated");

    await messagingService.deleteMessage(messageId, user.id);
  };

  const pinMessage = async (messageId: string) => {
    if (!user?.id) throw new Error("User not authenticated");

    await messagingService.pinMessage(messageId, user.id);
  };

  const markAsRead = async (conversationId: string) => {
    if (!user?.id) return;
    await messagingService.markAsRead(user.id, conversationId);
  };

  const getLeagueMembers = async (leagueId: string) => {
    return await messagingService.getLeagueMembers(leagueId);
  };

  const clearError = () => {
    dispatch({ type: "SET_ERROR", payload: null });
  };

  // Helper functions
  const getConversationId = (conversation: Conversation): string => {
    return conversation.id;
  };

  const getConversationTargetId = (conversation: Conversation): string => {
    if (conversation.type === "direct") {
      return conversation.contact?.id || "";
    } else {
      return conversation.league?.id || "";
    }
  };

  const contextValue: MessagingContextType = {
    state,
    actions: {
      loadConversations,
      loadContacts,
      selectConversation,
      sendDirectMessage,
      sendLeagueMessage,
      sendLeagueAnnouncement,
      setTyping,
      moderateMember,
      deleteMessage,
      pinMessage,
      markAsRead,
      getLeagueMembers,
      clearError,
    },
  };

  return (
    <MessagingContext.Provider value={contextValue}>
      {children}
    </MessagingContext.Provider>
  );
}

// Hook to use messaging context
export function useMessaging(): MessagingContextType {
  const context = useContext(MessagingContext);
  if (context === undefined) {
    throw new Error("useMessaging must be used within a MessagingProvider");
  }
  return context;
}
